import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import LoginProblemLinks from './LoginProblemLinks'
import { formatDeviseErrors, splitFieldErrors, fieldErrorText } from './deviseErrors'
import { deviseFetch, showFailedRequest } from './deviseRequest'
import useSubmitting from '../../hooks/useSubmitting'
import { useNavigate } from 'react-router-dom'
import {
  setFlashNotice,
  setFlashAlert,
  clearFlashNotice,
  clearFlashAlert,
} from '../../redux/features/ui/uiSlice'
import { selectAuthToken, setAuthToken } from '../../redux/features/user/userSlice'

const classes = {
}

export default function ResetPassword() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()
  const storedAuthToken = useSelector(selectAuthToken)
  const csrfToken = storedAuthToken || document.querySelector('meta[name=csrf-token]')?.content || ''

  useEffect(() => {
    if (!storedAuthToken) {
      const documentAuthToken = document.querySelector('meta[name=csrf-token]')?.content
      if (documentAuthToken) {
        dispatch(setAuthToken(documentAuthToken))
      }
    }
  }, [dispatch, storedAuthToken])

  const handleSubmit = (evt) => {
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)
    if (csrfToken) {
      data.set('authenticity_token', csrfToken)
    }

    submit(async () => {
      const result = await deviseFetch(dispatch, "/user/password.json", {
        method: 'post',
        body: data,
        headers: {
          'Accept': 'application/json',
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        }
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.status === 201) {
        dispatch(setFlashNotice(
          'Check your inbox. If that email is registered, we have sent password reset instructions.'
        ))
        navigate('/app/sign_in')
        return
      }

      // A 422 shows its errors beside the fields, with a summary in the flash
      // (which also holds any error without a field, or an unexpected status).
      const { fieldErrors, flashMessage } = response.status === 422
        ? splitFieldErrors(json_data, ['email'], 'the reset instructions from being sent')
        : { fieldErrors: {}, flashMessage: formatDeviseErrors(json_data) }
      setErrors(fieldErrors)
      dispatch(setFlashAlert(flashMessage))
    })
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component='h1' variant='h5'>
          Reset password
        </Typography>
        <Box 
          component="form"
          sx={{ mt: 1, width: '100%', maxWidth: 400 }}
          onSubmit={handleSubmit} 
          noValidate
        >
          <TextField
            margin='normal'
            required
            fullWidth
            id='user[email]'
            label='Email'
            name='user[email]'
            autoComplete='email'
            error={Boolean(errors.email)}
            helperText={fieldErrorText(errors, 'email')}
            autoFocus
          />
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Send password reset
          </Button>
        <LoginProblemLinks />
        </Box>
      </Box>
    </Container>
  )
}